import type { SubscriptionType } from '@/types/types';
import SearchIcon from '@mui/icons-material/Search';
import { Box, InputAdornment, MenuItem, Select, TextField, Typography } from '@mui/material';
import type { SubscriptionStatusLabel } from 'utils/kpiCalculations';

export type ProductFilter = SubscriptionType | 'ALL';
export type StatusFilter = SubscriptionStatusLabel | 'ALL';

// The allowed values as non-empty tuples, the shape the page's Zod URL parsing needs, typed rather
// than asserted (react-base/RULES.md § TypeScript: no `as`); the option lists derive from them, and
// each label record is keyed by the union, so the compiler flags a value with no label.
export const PRODUCT_FILTER_VALUES: [ProductFilter, ...ProductFilter[]] = [
  'ALL',
  'ELEGANCE',
  'E_MAX',
  'WATER_ONLY',
  'SHORT_RENTAL'
];
export const STATUS_FILTER_VALUES: [StatusFilter, ...StatusFilter[]] = [
  'ALL',
  'active',
  'ending_soon',
  'expired'
];

const PRODUCT_LABELS: Record<ProductFilter, string> = {
  ALL: 'All Products',
  ELEGANCE: 'Elegance',
  E_MAX: 'E-Max',
  WATER_ONLY: 'Water Only',
  SHORT_RENTAL: 'Short-Term Rental'
};
const STATUS_LABELS: Record<StatusFilter, string> = {
  ALL: 'All Statuses',
  active: 'Active',
  ending_soon: 'Ending Soon',
  expired: 'Expired'
};

export const PRODUCT_OPTIONS = PRODUCT_FILTER_VALUES.map(value => ({
  value,
  label: PRODUCT_LABELS[value]
}));
export const STATUS_OPTIONS = STATUS_FILTER_VALUES.map(value => ({
  value,
  label: STATUS_LABELS[value]
}));

// MUI's Select hands its value back as a plain string; these narrow it to the options above
// instead of asserting a type (react-base/RULES.md § TypeScript).
export const isProductFilter = (value: string): value is ProductFilter =>
  PRODUCT_OPTIONS.some(option => option.value === value);
export const isStatusFilter = (value: string): value is StatusFilter =>
  STATUS_OPTIONS.some(option => option.value === value);

interface SubscriptionsFilterBarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  productFilter: ProductFilter;
  onProductFilterChange: (value: ProductFilter) => void;
  statusFilter: StatusFilter;
  onStatusFilterChange: (value: StatusFilter) => void;
  totalElements: number;
}

// REQ-8: the search box, the product/status selects and the record count — split out of
// SubscriptionsPage so that page's own URL-filter wiring (react-base/RULES.md § State management)
// fits under max-lines; this component owns no state of its own, every value and handler comes
// from the page.
export const SubscriptionsFilterBar = ({
  searchValue,
  onSearchChange,
  productFilter,
  onProductFilterChange,
  statusFilter,
  onStatusFilterChange,
  totalElements
}: SubscriptionsFilterBarProps) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
    <TextField
      placeholder="Search name or address..."
      value={searchValue}
      onChange={e => onSearchChange(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" color="action" />
          </InputAdornment>
        )
      }}
      sx={{ maxWidth: 280 }}
    />
    <Select
      value={productFilter}
      onChange={e => {
        if (isProductFilter(e.target.value)) onProductFilterChange(e.target.value);
      }}
      size="small"
      sx={{ minWidth: 150 }}
    >
      {PRODUCT_OPTIONS.map(opt => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
    <Select
      value={statusFilter}
      onChange={e => {
        if (isStatusFilter(e.target.value)) onStatusFilterChange(e.target.value);
      }}
      size="small"
      sx={{ minWidth: 150 }}
    >
      {STATUS_OPTIONS.map(opt => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
    <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
      {totalElements} records
    </Typography>
  </Box>
);
